// src/lib/useDisclosureFocus.ts
//
// ★ A FORM OPENED FROM A CONTROL behaves like what it is (UAT re-run F-05): focus MOVES into it, Escape CLOSES it,
// and closing hands focus BACK to the control that opened it. Without the first, a keyboard user pressed Enter on
// "New draft of MSO-R4", saw nothing happen at the focus point and needed 16 Shift+Tab presses to find a form that
// sits earlier in the page than its button; without the second, Escape did nothing.
import { useEffect, type RefObject } from 'react'

const FIRST_FIELD = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'

/**
 * @param container the opened form
 * @param onClose   called on Escape. Ignored while a popover inside the form (the calendar) is open: that Escape
 *                  closes the popover, not the form.
 */
export function useDisclosureFocus(container: RefObject<HTMLElement | null>, onClose: () => void): void {
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    container.current?.querySelector<HTMLElement>(FIRST_FIELD)?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const form = container.current
      if (form === null || !form.contains(document.activeElement)) return
      if (form.querySelector('.pt-dp-pop') !== null) return
      event.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (opener !== null && opener !== document.body && opener.isConnected) opener.focus()
    }
    // Opened once per mount: a new `onClose` identity must not re-run the focus move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
